import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { BookOpen, Sparkles, Users, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function ClubSummary() {
    const { slug, cycleId } = useParams();
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/clubs/${slug}/cycles/${cycleId}/summary`);
                if (alive) setData(data);
            } catch (e) {
                if (alive) setError(formatApiError(e.response?.data?.detail));
            }
        })();
        return () => { alive = false; };
    }, [slug, cycleId]);

    const handleShare = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({ title: data?.summary?.headline, url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success(t("clubs.summary_copied"));
            }
        } catch { /* user cancelled */ }
    };

    if (error) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex items-center justify-center px-6 text-center">
                <p className="text-[var(--ink-muted)]">{error}</p>
            </main>
        );
    }
    if (!data) {
        return (
            <main className="min-h-[calc(100vh-73px)] max-w-4xl mx-auto px-6 md:px-12 py-24">
                <p className="font-display text-2xl italic text-[var(--ink)] animate-slow-pulse">…</p>
            </main>
        );
    }

    const { club, cycle, summary, participant_count: pc, pending_reason: pending } = data;

    if (!summary) {
        return (
            <main className="min-h-[calc(100vh-73px)] max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
                <BookOpen size={36} strokeWidth={1} className="mx-auto text-[var(--accent)] mb-4" />
                <h1 className="font-display text-3xl text-[var(--ink)] mb-3"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}>
                    {pending === "deadline_not_reached"
                        ? t("clubs.summary_pending_deadline")
                        : t("clubs.summary_pending_no_subs")}
                </h1>
                <p className="text-[var(--ink-muted)] mb-8 max-w-md mx-auto">
                    {pending === "deadline_not_reached"
                        ? t("clubs.summary_pending_deadline_lead")
                        : t("clubs.summary_pending_no_subs_lead")}
                </p>
                <Link to={`/clubs/${slug}`} className="border border-[var(--ink)] text-[var(--ink)] rounded-full hover:bg-[var(--bg-hover)] transition-colors px-6 py-2.5 text-sm uppercase tracking-widest">
                    {t("clubs.back_to_club")}
                </Link>
            </main>
        );
    }

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
            <Helmet>
                <title>{summary.headline} · {club.name}</title>
                <meta property="og:title" content={summary.headline} />
                <meta property="og:description" content={`${club.name} · ${cycle.book?.title}`} />
            </Helmet>

            <Link to={`/clubs/${slug}`} className="text-xs uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors mb-6 inline-block">
                ← {club.name}
            </Link>

            <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                {t("clubs.cycle_n", { n: cycle.cycle_number })} · {cycle.book?.title}
            </p>
            <h1
                className="font-display text-5xl md:text-7xl text-[var(--ink)] tracking-tight mb-6"
                style={{ fontVariationSettings: '"wght" 700, "wdth" 78, "opsz" 144' }}
                data-testid="summary-headline"
            >
                {summary.headline}
            </h1>

            <div className="flex items-center gap-6 text-xs text-[var(--ink-dim)] mb-12">
                <span className="inline-flex items-center gap-1.5"><Users size={12} strokeWidth={1.5} /> {t("clubs.summary_participants", { count: pc })}</span>
                <span>{new Date(data.generated_at).toLocaleDateString(i18n.language)}</span>
                <button onClick={handleShare} className="inline-flex items-center gap-1.5 hover:text-[var(--accent)] transition-colors uppercase tracking-widest">
                    <Share2 size={12} strokeWidth={1.5} /> {t("clubs.share")}
                </button>
            </div>

            {/* Curiosities */}
            <section className="mb-16">
                <h2 className="font-display text-3xl md:text-4xl text-[var(--ink)] mb-6 inline-flex items-center gap-3"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 64' }}>
                    <Sparkles size={20} strokeWidth={1.5} className="text-[var(--accent)]" />
                    {t("clubs.summary_curiosities")}
                </h2>
                <ul className="space-y-3" data-testid="summary-curiosities">
                    {(summary.curiosities || []).map((c, i) => (
                        <li key={i} className="flex gap-4 text-[var(--ink-muted)] leading-relaxed">
                            <span className="font-mono-num text-xs text-[var(--accent)] mt-1.5 w-6">{(i + 1).toString().padStart(2, "0")}</span>
                            <span>{c}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* Themes */}
            <section className="mb-16">
                <h2 className="font-display text-3xl md:text-4xl text-[var(--ink)] mb-6"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 64' }}>
                    {t("clubs.summary_themes")}
                </h2>
                <div className="space-y-8" data-testid="summary-themes">
                    {(summary.themes || []).map((th, i) => (
                        <article key={i} className="border-l-2 border-[var(--accent)] pl-6">
                            <h3 className="font-display text-xl text-[var(--ink)] mb-2"
                                style={{ fontVariationSettings: '"wght" 600, "wdth" 92, "opsz" 24' }}>
                                {th.title}
                            </h3>
                            <p className="text-[var(--ink-muted)] leading-relaxed">{th.summary}</p>
                        </article>
                    ))}
                </div>
            </section>

            {summary.closing && (
                <p className="font-display text-2xl md:text-3xl italic text-[var(--accent)] text-center max-w-2xl mx-auto"
                   style={{ fontVariationSettings: '"wght" 400, "wdth" 95, "opsz" 32, "slnt" -10' }}>
                    {summary.closing}
                </p>
            )}
        </main>
    );
}
